import SvgIcon from 'components/Icon/SvgIcon'
import PMBTooltip from 'components/PMBTooltip'
import SidebarDetails from 'components/SidebarDetails'
import { AbilityContext } from 'context/Acl'
import { useFetchApprovalCountQuery } from 'features/private-views/approval/approvalApiSlice'
import { setCountTag } from 'features/private-views/approval/approvalSlice'
import { toggleNavbar } from 'features/private-views/dashboard/dashboardSlice'
import { getLateInvoice } from 'features/private-views/talangin-invoice/talanginInvoiceAction'
import { getMetaContent } from 'helpers'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Button, Container, Nav, NavItem } from 'reactstrap'
import {
  BUSINESS_BOOKKEEPING,
  BUSINESS_MYAPPROVAL,
  BUSINESS_TALANGIN,
  READ,
} from 'utils/subjectActions'
import P from '../../components/Text/index'
import Collapsible from './Collapsible'
import { menus } from './menus'
import './index.css'
import useCompanyLogo from 'hooks/useCompanyLogo'

const addIsOpenToMenus = (menus, currentPath = window.location.pathname) => {
  return menus.map((menu) => {
    const hasActiveChild = menu?.items?.some((item) => {
      if (!item?.path) return false
      if (item.path === '/') return currentPath === '/'
      return (
        currentPath === item.path || currentPath.startsWith(item.path + '/')
      )
    })
    return {
      ...menu,
      isOpen: Boolean(hasActiveChild),
    }
  })
}

const NavbarSide = () => {
  const navigate = useNavigate()
  let location = useLocation()
  const dispatch = useDispatch()
  const ability = useContext(AbilityContext)
  const { data, isLoading, isSuccess } = useFetchApprovalCountQuery()
  const { isOpenNavbar } = useSelector((s) => s.dashboard)
  const { lateInvoice } = useSelector((s) => s.talanginInvoice)
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const { serviceProvision } = useSelector((s) => s.auth)

  const [pinnedItems, setPinnedItems] = useState([])
  const { ownerData } = useSelector((s) => s.dashboard)
  const ownerUserId = ownerData?.userId
  const { logoSrc } = useCompanyLogo()
  const currentYear = new Date().getFullYear()
  const copyrightYearText =
    currentYear > 2020 ? `2020 - ${currentYear}` : '2020'

  // localStorage key
  const getStorageKey = useCallback(
    (userId) => `pinnedWorkplaceItems-${userId}`,
    [],
  )

  const safeParseJSON = useCallback((jsonString) => {
    try {
      return JSON.parse(jsonString)
    } catch (error) {
      console.error('Error parsing JSON from localStorage:', error)
      return null
    }
  }, [])

  const safeSetLocalStorage = useCallback((key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data))
      return true
    } catch (error) {
      console.error('Error saving to localStorage:', error)
      return false
    }
  }, [])

  useEffect(() => {
    if (!ownerUserId) return

    const storageKey = getStorageKey(ownerUserId)

    try {
      const stored = localStorage.getItem(storageKey)

      if (stored) {
        const parsed = safeParseJSON(stored)

        if (parsed && Array.isArray(parsed)) {
          const validItems = parsed.filter(
            (item) =>
              item &&
              typeof item === 'object' &&
              item.value &&
              item.label &&
              item.path,
          )

          // FILTER OUT FINDORA ITEMS IF SERVICE PROVISION IS NOT AVAILABLE
          const filteredItems = validItems.filter((item) => {
            // CHECK IF ITEM IS FROM FINDORA DATA BY CHECKING COMMON FINDORA VALUES
            const isFindoraItem = [
              'product-mapping-schema',
              'findora-logs',
              'product-mapping',
            ].includes(item.value)

            // IF IT'S FINDORA ITEM AND SERVICE PROVISION IS FALSE, EXCLUDE IT
            if (isFindoraItem && serviceProvision?.finProdMapping !== true) {
              return false
            }

            return true
          })

          setPinnedItems(filteredItems)

          // UPDATE LOCALSTORAGE IF ITEMS WERE FILTERED OUT
          if (filteredItems.length !== validItems.length) {
            if (filteredItems.length === 0) {
              localStorage.removeItem(storageKey)
            } else {
              safeSetLocalStorage(storageKey, filteredItems)
            }
          }
        } else {
          console.warn('NavbarSide - Invalid pinned items data in localStorage')
          localStorage.removeItem(storageKey)
        }
      }
    } catch (error) {
      console.error('NavbarSide - Error loading pinned items:', error)
      try {
        localStorage.removeItem(storageKey)
      } catch (removeError) {
        console.error(
          'NavbarSide - Error removing corrupted localStorage item:',
          removeError,
        )
      }
    }
  }, [
    ownerUserId,
    getStorageKey,
    safeParseJSON,
    serviceProvision?.finProdMapping,
    safeSetLocalStorage,
  ])

  useEffect(() => {
    if (!ownerUserId) return

    const storageKey = getStorageKey(ownerUserId)

    const handleStorageChange = (e) => {
      if (e.key === storageKey) {
        try {
          const newValue = e.newValue ? JSON.parse(e.newValue) : []

          // FILTER OUT FINDORA ITEMS IF SERVICE PROVISION IS NOT AVAILABLE
          const filteredItems = newValue.filter((item) => {
            const isFindoraItem = [
              'product-mapping-schema',
              'findora-logs',
              'product-mapping',
            ].includes(item.value)

            if (isFindoraItem && serviceProvision?.finProdMapping !== true) {
              return false
            }

            return true
          })

          setPinnedItems(filteredItems)
        } catch (error) {
          console.error('Error parsing storage update:', error)
        }
      }
    }

    const handlePinnedItemsChange = (event) => {
      if (event.detail.userId === ownerUserId) {
        // FILTER OUT FINDORA ITEMS IF SERVICE PROVISION IS NOT AVAILABLE
        const filteredItems = event.detail.pinnedItems.filter((item) => {
          const isFindoraItem = [
            'product-mapping-schema',
            'findora-logs',
            'product-mapping',
          ].includes(item.value)

          if (isFindoraItem && serviceProvision?.finProdMapping !== true) {
            return false
          }

          return true
        })

        setPinnedItems(filteredItems)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('pinnedItemsChanged', handlePinnedItemsChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('pinnedItemsChanged', handlePinnedItemsChange)
    }
  }, [ownerUserId, getStorageKey, serviceProvision?.finProdMapping])

  const finalMenus = useMemo(() => {
    if (pinnedItems?.length > 0) {
      const pinnedMenu = {
        headTitle: 'Favorit',
        action: READ,
        icon: <SvgIcon name="old-icons-star" size={30} id="favorit" />,
        subject: [BUSINESS_BOOKKEEPING],
        items: pinnedItems
          ?.sort((a, b) => (b.pinnedAt || 0) - (a.pinnedAt || 0))
          ?.map((item) => ({
            title: item?.label,
            icon: (
              <SvgIcon
                name={item?.iconName}
                size={30}
                id={item?.iconName}
                color="#667085"
              />
            ),
            path: item?.path,
            action: item?.action,
            subject: item?.subject,
          })),
        isOpen: false,
      }

      return [...menus, pinnedMenu]
    }

    return menus
  }, [pinnedItems])

  const menu = addIsOpenToMenus(finalMenus, location.pathname)
  const [menuState, setMenuState] = useState(menu)

  useEffect(() => {
    setMenuState(addIsOpenToMenus(finalMenus, location.pathname))
  }, [finalMenus, location.pathname])

  useEffect(() => {
    setMenuState((prevMenus) =>
      prevMenus.map((m) => {
        const hasActiveChild = m?.items?.some((item) => {
          if (!item?.path) return false
          if (item.path === '/') return location.pathname === '/'
          return (
            location.pathname === item.path ||
            location.pathname.startsWith(item.path + '/')
          )
        })
        if (hasActiveChild && !m.isOpen) {
          return { ...m, isOpen: true }
        }
        return m
      }),
    )
  }, [location.pathname])

  useEffect(() => {
    if (isSuccess) dispatch(setCountTag(data?.data))
  }, [dispatch, data, isSuccess])

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [dispatch])

  const isMobile = screenWidth < 768
  const [isHovered, setIsHovered] = useState(false)
  const hoverTimeoutRef = useRef(null)
  const isHoverBlockedRef = useRef(false)
  const hoverBlockTimeoutRef = useRef(null)

  const isExpanded = isOpenNavbar || (!isMobile && isHovered)

  const blockHoverTemporarily = useCallback((duration = 500) => {
    isHoverBlockedRef.current = true
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    if (hoverBlockTimeoutRef.current) {
      clearTimeout(hoverBlockTimeoutRef.current)
    }
    setIsHovered(false)
    hoverBlockTimeoutRef.current = setTimeout(() => {
      isHoverBlockedRef.current = false
      hoverBlockTimeoutRef.current = null
    }, duration)
  }, [])

  const handleMouseEnter = useCallback(() => {
    if (isHoverBlockedRef.current) {
      return
    }
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    if (!isMobile && !isOpenNavbar) {
      setIsHovered(true)
      if (
        document.activeElement &&
        typeof document.activeElement.blur === 'function'
      ) {
        document.activeElement.blur()
      }
    }
  }, [isMobile, isOpenNavbar])

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false)
      isHoverBlockedRef.current = false
    }, 200)
  }, [])

  useEffect(() => {
    if (isOpenNavbar) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
        hoverTimeoutRef.current = null
      }
      if (hoverBlockTimeoutRef.current) {
        clearTimeout(hoverBlockTimeoutRef.current)
        hoverBlockTimeoutRef.current = null
      }
      setIsHovered(false)
      isHoverBlockedRef.current = false
    }
  }, [isOpenNavbar])

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      if (hoverBlockTimeoutRef.current) {
        clearTimeout(hoverBlockTimeoutRef.current)
      }
    }
  }, [])

  const onClickMenuItem = () => {
    if (isMobile && isOpenNavbar) {
      dispatch(toggleNavbar())
    }
  }

  const onClickToggle = (index) => {
    setMenuState((prevMenus) =>
      prevMenus.map((menu, idx) =>
        idx === index ? { ...menu, isOpen: !menu.isOpen } : menu,
      ),
    )
  }

  const getDefaultPath = () => {
    for (const menu of menuState) {
      const hasAccess =
        menu?.ignoreAbility ||
        menu?.subject?.some((sub) => ability.can(menu.action, sub))

      if (hasAccess) {
        if (menu.isParent && menu.path) {
          return menu.path
        }
        
        if (menu.items?.length) {
          for (const item of menu.items || []) {
            const hasAccessItem = item?.subject?.some((sub) =>
              ability.can(item.action, sub),
            )
            if (hasAccessItem) {
              return item.path
            }
          }
        }
      }
    }

    return '/'
  }

  useEffect(() => {
    if (location.pathname === '/') {
      const defaultPath = getDefaultPath()
      if (defaultPath !== '/') {
        navigate(defaultPath, { replace: true })
      }
    }
  }, [location, menuState])

  useEffect(() => {
    dispatch(getLateInvoice())
  }, [])

  const ShowHideButton = ({ customStyle }) => {
    return (
      <div
        className="d-flex justify-content-center"
        style={{
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          backgroundColor: 'white',
          padding: '5px',
          borderRadius: '20px',
          ...customStyle,
        }}
      >
        <Button
          size="sm"
          style={{
            width: '25px',
            height: '25px',
            boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
          }}
          outline
          className="rounded-circle d-flex flex-column align-items-center border-0"
          color="secondary"
          data-testid="sidebar-toggle"
          onClick={() => dispatch(toggleNavbar())}
        >
          {isOpenNavbar ? (
            <ChevronLeft size={25} />
          ) : (
            <ChevronRight size={25} />
          )}
        </Button>
      </div>
    )
  }

  return (
    <>
      {isMobile ? (
        <SidebarDetails
          open={isOpenNavbar}
          contentClassName="pt-0"
          isNavbar
          isBack={false}
          toggleSidebar={() => dispatch(toggleNavbar())}
          title={
            <div className="d-flex align-items-center">
              <ShowHideButton customStyle={{ marginLeft: 15 }} />
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ cursor: 'pointer' }}
                data-testid="sidebar-brand"
                // onClick={() => navigate('/homepage')}
                onClick={() => navigate('/')}
              >
                <img
                  alt={`${getMetaContent('og:title')} logo`}
                  src={logoSrc}
                  style={{
                    width: '100%',
                    maxWidth: '130px',
                    maxHeight: '36px',
                    height: 'auto',
                    objectFit: 'contain',
                  }}
                />
                <P size="regular" className="text-center mt-1 mb-0">
                  <span className="text-xs text-muted" style={{ fontSize: '11px', lineHeight: '1.2' }}>
                    Versi: {process.env.REACT_APP_VERSION}
                  </span>
                </P>
              </div>
            </div>
          }
          noClose
          isLeftAnimation
        >
          <div
            className="no-scrollbar"
            style={{
              paddingBottom: '60px',
            }}
          >
            <Nav vertical className="list-unstyled pb-3">
              {menuState?.map((e, i) => {
                const hasAccess =
                  e?.ignoreAbility ||
                  e?.subject?.some((sub) => ability.can(e.action, sub))
                if (e?.action && !e?.isParent) {
                  if (hasAccess) {
                    return (
                      <div
                        key={`mobile-menu-${i}`}
                        className="d-flex flex-column gap-2"
                      >
                        <div
                          onClick={() => onClickToggle(i)}
                          className={`d-flex justify-content-between pointer align-items-center ${e?.isOpen ? 'nav-active' : ''}`}
                          data-testid={`sidebar-mobile-menu-${i}`}
                          style={{
                            padding: 10,
                          }}
                        >
                          <div className="d-flex align-items-center">
                            {React.cloneElement(e.icon, {
                              color: e?.isOpen ? '#344054' : '#667085',
                            })}
                            <P
                              size="regular"
                              bold={e?.isOpen ? 600 : 500}
                              className="px-2 mt-1"
                              style={{
                                color: e?.isOpen ? '#101828' : '#344054',
                              }}
                            >
                              {e.headTitle}
                            </P>
                          </div>
                          {e?.isOpen ? (
                            <ChevronDown size={20} />
                          ) : (
                            <ChevronRight color="#667085" size={20} />
                          )}
                        </div>
                        <Collapsible isOpen={e?.isOpen}>
                          {e?.items?.map((item, j) => {
                            if (item?.action) {
                              const hasAccess2 =
                                item?.ignoreAbility ||
                                item?.subject?.some((sub) =>
                                  ability.can(item.action, sub),
                                )
                              if (hasAccess2) {
                                return (
                                  <React.Fragment key={`mobile-item-${i}-${j}`}>
                                    <NavItem
                                      className="hover rounded mb-2"
                                      id={`tooltip-${i}-${j}`}
                                    >
                                      <NavLink
                                        className={({ isActive }) =>
                                          isActive
                                            ? !isOpenNavbar
                                              ? 'nav-active justify-content-center'
                                              : 'nav-active'
                                            : !isActive && !isOpenNavbar
                                              ? 'nav-unactive justify-content-center'
                                              : 'nav-unactive'
                                        }
                                        to={item?.path}
                                        data-testid={`sidebar-mobile-item-${i}-${j}`}
                                        onClick={() => onClickMenuItem()}
                                        style={{
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {isOpenNavbar && (
                                          <P
                                            size="regular"
                                            bold={isOpenNavbar ? 600 : 500}
                                            style={{
                                              paddingLeft: 40,
                                            }}
                                          >
                                            {item?.title}
                                          </P>
                                        )}
                                        {item?.subject?.[0] ===
                                          BUSINESS_MYAPPROVAL &&
                                          item?.subject?.length === 1 &&
                                          data?.data.length > 0 &&
                                          isOpenNavbar && (
                                            <span
                                              style={{
                                                height: 22,
                                                width: 'auto',
                                                textDecoration: 'none',
                                              }}
                                              className="rounded-circle badge-cancel flex-centered text-sm"
                                            >
                                              {isSuccess
                                                ? data?.data
                                                    ?.map((e) => e.total)
                                                    ?.reduce(
                                                      (sum, e) => sum + e,
                                                      0,
                                                    )
                                                : isLoading
                                                  ? 0
                                                  : 0}
                                            </span>
                                          )}
                                        {item?.subject?.[0] ===
                                          BUSINESS_TALANGIN &&
                                          item?.subject?.length === 1 &&
                                          lateInvoice?.allLate > 0 &&
                                          isOpenNavbar && (
                                            <span
                                              style={{
                                                height: 22,
                                                width: 'auto',
                                                textDecoration: 'none',
                                              }}
                                              className="rounded-circle badge-cancel flex-centered text-sm"
                                            >
                                              {lateInvoice?.allLate ?? 0}
                                            </span>
                                          )}
                                      </NavLink>
                                    </NavItem>
                                    {!isOpenNavbar && (
                                      <PMBTooltip
                                        placement="right"
                                        target={`tooltip-${i}-${j}`}
                                        text={item?.title}
                                      />
                                    )}
                                  </React.Fragment>
                                )
                              }
                            }
                            return null
                          })}
                        </Collapsible>
                      </div>
                    )
                  }
                } else if (e?.isParent) {
                  // Check permissions if exists
                  if (e?.action && e?.subject) {
                    const hasAccess =
                      e?.ignoreAbility ||
                      e.subject.some((sub) => ability.can(e.action, sub))
                    if (!hasAccess) return null
                  }
                  return (
                    <div
                      key={`mobile-parent-${i}`}
                      className="d-flex flex-column"
                    >
                      <div className="pb-2">
                        <NavLink
                          className={({ isActive }) =>
                            isActive
                              ? !isOpenNavbar
                                ? 'nav-active justify-content-center'
                                : 'nav-active'
                              : !isActive && !isOpenNavbar
                                ? 'nav-unactive justify-content-center'
                                : 'nav-unactive'
                          }
                          to={e?.path}
                          onClick={() => onClickMenuItem()}
                          style={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                        >
                          <div className="d-flex align-items-center ms-1 w-100">
                            {React.cloneElement(e.icon, {
                              color:
                                e?.path === location?.pathname
                                  ? '#344054'
                                  : '#667085',
                            })}
                            <P
                              size="regular"
                              bold={e?.path === location?.pathname ? 600 : 500}
                              className="mt-1 px-2"
                              style={{
                                color:
                                  e?.path === location?.pathname
                                    ? '#101828'
                                    : '#344054',
                              }}
                            >
                              {e.headTitle}
                            </P>
                          </div>
                        </NavLink>
                      </div>
                    </div>
                  )
                }
                return null
              })}
            </Nav>
          </div>
          <div
            className="d-flex flex-column align-items-center justify-content-center text-center"
            style={{
              padding: '10px 16px',
              borderTop: '1px solid #f2f4f7',
              flexShrink: 0,
              backgroundColor: '#ffffff',
            }}
          >
            <span
              className="text-xs"
              style={{
                fontSize: '11px',
                fontWeight: 500,
                color: '#475467',
                lineHeight: '1.4',
              }}
              data-testid="sidebar-mobile-footer-powered-by"
            >
              Powered by <span className="fw-semibold text-dark">PasarMIKRO</span>
            </span>
            <span
              className="text-xs text-muted"
              style={{
                fontSize: '10px',
                color: '#98a2b3',
                lineHeight: '1.4',
              }}
              data-testid="sidebar-mobile-footer-copyright"
            >
              © {copyrightYearText}
            </span>
            <span
              className="text-xs text-muted"
              style={{
                fontSize: '10px',
                color: '#98a2b3',
                lineHeight: '1.4',
              }}
              data-testid="sidebar-mobile-footer-version"
            >
              Versi: {process.env.REACT_APP_VERSION || '1.0.0'}
            </span>
          </div>
        </SidebarDetails>
      ) : (
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className="sidebar-desktop-container bg-white border border-top-0"
          style={{
            width: isExpanded ? '280px' : '80px',
            paddingTop: '75px',
            position: 'fixed',
            top: '0',
            left: '0',
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            zIndex: 1020,
            boxShadow:
              !isOpenNavbar && isExpanded
                ? '0 10px 30px rgba(0, 0, 0, 0.12)'
                : 'none',
          }}
        >
          {/* Collapse / Expand Toggle Button at Top Right Border */}
          <button
            type="button"
            data-testid="sidebar-toggle"
            className="sidebar-toggle-btn"
            title={isOpenNavbar ? 'Kecilkan sidebar' : 'Buka sidebar'}
            onClick={(e) => {
              e.stopPropagation()
              if (isOpenNavbar) {
                blockHoverTemporarily(500)
              }
              dispatch(toggleNavbar())
            }}
          >
            {isOpenNavbar ? (
              <ChevronLeft size={16} color="#475467" />
            ) : (
              <ChevronRight size={16} color="#475467" />
            )}
          </button>
          <div
            className="no-scrollbar"
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              paddingBottom: '10px',
            }}
          >
            <Nav vertical className="list-unstyled pb-3 mt-2">
              <Container>
                {menuState?.map((e, i) => {
                  const hasAccess =
                    e?.ignoreAbility ||
                    e?.subject?.some((sub) => ability.can(e.action, sub))
                  if (e?.action && !e?.isParent) {
                    if (hasAccess) {
                      const isChildActive = Boolean(
                        e?.items?.some((item) => {
                          if (!item?.path) return false
                          if (item.path === '/') return location?.pathname === '/'
                          return (
                            location?.pathname === item.path ||
                            location?.pathname?.startsWith(item.path + '/')
                          )
                        }),
                      )

                      const isRowActive = !isExpanded
                        ? isChildActive
                        : isChildActive && !e?.isOpen
                      const isRowOpenActive = isExpanded && isChildActive && e?.isOpen
                      const isRowOpen = isExpanded && !isChildActive && e?.isOpen

                      const rowClassName = isRowActive
                        ? 'sidebar-menu-row nav-active'
                        : isRowOpenActive
                          ? 'sidebar-menu-row nav-open-active'
                          : isRowOpen
                            ? 'sidebar-menu-row nav-open'
                            : 'sidebar-menu-row'

                      const iconColor = isChildActive
                        ? '#006386'
                        : e?.isOpen
                          ? '#344054'
                          : '#667085'

                      const textColor = isChildActive
                        ? '#006386'
                        : e?.isOpen
                          ? '#101828'
                          : '#344054'

                      const chevronColor = isChildActive
                        ? '#006386'
                        : e?.isOpen
                          ? '#344054'
                          : '#667085'

                      return (
                        <div
                          key={`desktop-menu-${i}`}
                          className="d-flex flex-column gap-1 mb-1"
                        >
                          <div id={`tooltip-${i}`}>
                            <div
                              className={rowClassName}
                              onClick={() => onClickToggle(i)}
                              style={{
                                padding: '8px 16px',
                              }}
                            >
                              <div className="sidebar-icon-box">
                                {React.cloneElement(e.icon, {
                                  color: iconColor,
                                })}
                              </div>
                              <div
                                className={
                                  isExpanded
                                    ? 'sidebar-text-expand'
                                    : 'sidebar-text-collapse'
                                }
                              >
                                <P
                                  size="regular"
                                  bold={isChildActive || e?.isOpen ? 600 : 500}
                                  className="m-0"
                                  style={{
                                    color: textColor,
                                  }}
                                >
                                  {e.headTitle}
                                </P>
                                <div className="flex-shrink-0 ms-1">
                                  {e?.isOpen ? (
                                    <ChevronDown size={18} color={chevronColor} />
                                  ) : (
                                    <ChevronRight color={chevronColor} size={18} />
                                  )}
                                </div>
                              </div>
                            </div>
                            {!isExpanded && (
                              <PMBTooltip
                                placement="right"
                                target={`tooltip-${i}`}
                                text={`${e?.headTitle}`}
                              />
                            )}
                          </div>

                          <Collapsible isOpen={Boolean(e?.isOpen && isExpanded)}>
                            {e?.items?.map((item, j) => {
                              if (item?.action) {
                                const hasAccess2 =
                                  item?.ignoreAbility ||
                                  item?.subject?.some((sub) =>
                                    ability.can(item.action, sub),
                                  )

                                if (hasAccess2) {
                                  const isSubItemActive =
                                    item?.path === '/'
                                      ? location?.pathname === '/'
                                      : Boolean(
                                          item?.path &&
                                            (location?.pathname === item?.path ||
                                              location?.pathname?.startsWith(item?.path + '/')),
                                        )

                                  return (
                                    <React.Fragment
                                      key={`desktop-item-${i}-${j}`}
                                    >
                                      <NavItem
                                        className="hover rounded mb-1"
                                        id={`tooltip-${i}-${j}`}
                                      >
                                        <NavLink
                                          className={({ isActive }) =>
                                            isActive || isSubItemActive
                                              ? 'sidebar-menu-row nav-active'
                                              : 'sidebar-menu-row nav-unactive'
                                          }
                                          to={item?.path}
                                          data-testid={`sidebar-menu-item-${i}-${j}`}
                                          onClick={() => onClickMenuItem()}
                                          style={{
                                            padding: '8px 16px',
                                          }}
                                        >
                                          <div
                                            className="sidebar-icon-box"
                                            style={{
                                              paddingLeft: '6px',
                                            }}
                                          >
                                            {React.cloneElement(item?.icon, {
                                              color: isSubItemActive
                                                ? '#006386'
                                                : '#667085',
                                              size: 20,
                                            })}
                                          </div>
                                          <div
                                            className={
                                              isExpanded
                                                ? 'sidebar-text-expand'
                                                : 'sidebar-text-collapse'
                                            }
                                          >
                                            <P
                                              size="regular"
                                              bold={isSubItemActive ? 600 : 500}
                                              className="m-0"
                                              style={{
                                                color: isSubItemActive
                                                  ? '#006386'
                                                  : '#344054',
                                              }}
                                            >
                                              {item?.title}
                                            </P>
                                            {item?.subject?.[0] ===
                                              BUSINESS_MYAPPROVAL &&
                                              item?.subject?.length === 1 &&
                                              data?.data?.length > 0 &&
                                              isExpanded && (
                                                <span
                                                  style={{
                                                    height: 22,
                                                    width: 'auto',
                                                    textDecoration: 'none',
                                                  }}
                                                  className="rounded-circle badge-cancel flex-centered text-sm ms-auto"
                                                >
                                                  {isSuccess
                                                    ? data?.data
                                                        ?.map((it) => it.total)
                                                        ?.reduce(
                                                          (sum, it) => sum + it,
                                                          0,
                                                        )
                                                    : 0}
                                                </span>
                                              )}
                                            {item?.subject?.[0] ===
                                              BUSINESS_TALANGIN &&
                                              item?.subject?.length === 1 &&
                                              lateInvoice?.allLate > 0 &&
                                              isExpanded && (
                                                <span
                                                  style={{
                                                    height: 22,
                                                    width: 'auto',
                                                    textDecoration: 'none',
                                                  }}
                                                  className="rounded-circle badge-cancel flex-centered text-sm ms-auto"
                                                >
                                                  {lateInvoice?.allLate ?? 0}
                                                </span>
                                              )}
                                          </div>
                                        </NavLink>
                                      </NavItem>
                                      {!isExpanded && (
                                        <PMBTooltip
                                          placement="right"
                                          target={`tooltip-${i}-${j}`}
                                          text={item?.title}
                                        />
                                      )}
                                    </React.Fragment>
                                  )
                                }
                              }
                              return null
                            })}
                          </Collapsible>
                        </div>
                      )
                    }
                  } else if (e?.isParent) {
                    // Check permissions if exists
                    if (e?.action && e?.subject) {
                      const hasAccess =
                        e?.ignoreAbility ||
                        e.subject.some((sub) => ability.can(e.action, sub))
                      if (!hasAccess) return null
                    }

                    // PARENT MENU
                    const isParentActive =
                      e?.path === '/'
                        ? location?.pathname === '/'
                        : Boolean(
                            e?.path &&
                              (location?.pathname === e?.path ||
                                location?.pathname?.startsWith(e?.path + '/')),
                          )

                    return (
                      <div
                        key={`desktop-parent-${i}`}
                        className="d-flex flex-column mb-1"
                      >
                        <div id={`tooltip-${i}`}>
                          <NavLink
                            className={({ isActive }) =>
                              isActive || isParentActive
                                ? 'sidebar-menu-row nav-active'
                                : 'sidebar-menu-row nav-unactive'
                            }
                            to={e?.path}
                            onClick={() => onClickMenuItem()}
                            style={{
                              padding: '8px 16px',
                            }}
                          >
                            <div className="sidebar-icon-box">
                              {React.cloneElement(e.icon, {
                                color: isParentActive ? '#006386' : '#667085',
                              })}
                            </div>
                            <div
                              className={
                                isExpanded
                                  ? 'sidebar-text-expand'
                                  : 'sidebar-text-collapse'
                              }
                            >
                              <P
                                size="regular"
                                bold={isParentActive ? 600 : 500}
                                className="m-0"
                                style={{
                                  color: isParentActive ? '#006386' : '#344054',
                                }}
                              >
                                {e.headTitle}
                              </P>
                            </div>
                          </NavLink>
                          {!isExpanded && (
                            <PMBTooltip
                              placement="right"
                              target={`tooltip-${i}`}
                              text={e?.headTitle}
                            />
                          )}
                        </div>
                      </div>
                    )
                  }
                  return null
                })}
              </Container>
            </Nav>
          </div>
          {/* Footer: Powered by PasarMIKRO, Copyright & Version */}
          <div
            className="d-flex flex-column align-items-center justify-content-center text-center"
            style={{
              padding: isExpanded ? '10px 16px' : '12px 8px',
              borderTop: '1px solid #f2f4f7',
              flexShrink: 0,
              minHeight: '44px',
              backgroundColor: '#ffffff',
            }}
          >
            {isExpanded ? (
              <>
                <span
                  className="text-xs"
                  style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#475467',
                    lineHeight: '1.4',
                  }}
                  data-testid="sidebar-footer-powered-by"
                >
                  Powered by <span className="fw-semibold text-dark">PasarMIKRO</span>
                </span>
                <span
                  className="text-xs text-muted"
                  style={{
                    fontSize: '10px',
                    color: '#98a2b3',
                    lineHeight: '1.4',
                  }}
                  data-testid="sidebar-footer-copyright"
                >
                  © {copyrightYearText}
                </span>
                <span
                  className="text-xs text-muted"
                  style={{
                    fontSize: '10px',
                    color: '#98a2b3',
                    lineHeight: '1.4',
                  }}
                  data-testid="sidebar-footer-version"
                >
                  Versi: {process.env.REACT_APP_VERSION || '1.0.0'}
                </span>
              </>
            ) : (
              <span
                className="text-xs text-muted"
                style={{
                  fontSize: '11px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 500,
                  color: '#98a2b3',
                }}
                title={`Powered by PasarMIKRO\n© ${copyrightYearText}\nVersi: ${process.env.REACT_APP_VERSION || '1.0.0'}`}
                data-testid="sidebar-footer-version-collapsed"
              >
                v{process.env.REACT_APP_VERSION || '1.0.0'}
              </span>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default NavbarSide
